import { useEffect } from 'react';

import { SITE_URL } from './slugs';

interface Props {
    title: string;
    description: string;
    /** The path of this page, e.g. `/free-birth-chart`. */
    path: string;
}

const setMeta = (selector: string, make: () => HTMLElement, value: string) => {
    let el = document.head.querySelector<HTMLElement>(selector);
    if (!el) {
        el = make();
        document.head.appendChild(el);
    }
    const previous = el.getAttribute('content') ?? el.getAttribute('href');
    if (el.tagName === 'LINK') el.setAttribute('href', value);
    else el.setAttribute('content', value);
    return previous;
};

const meta = (attr: string, key: string) => () => {
    const el = document.createElement('meta');
    el.setAttribute(attr, key);
    return el;
};

/**
 * Gives the page its own title, description and canonical address. The app is client-rendered, so these are set
 * when the page mounts (crawlers that run scripts read them) and put back when it unmounts.
 */
export const Seo = ({ title, description, path }: Props): null => {
    useEffect(() => {
        const url = `${SITE_URL}${path}`;
        const oldTitle = document.title;
        document.title = title;
        const restore = [
            [
                'meta[name="description"]',
                meta('name', 'description'),
                description,
            ],
            ['meta[property="og:title"]', meta('property', 'og:title'), title],
            [
                'meta[property="og:description"]',
                meta('property', 'og:description'),
                description,
            ],
            ['meta[property="og:url"]', meta('property', 'og:url'), url],
            [
                'link[rel="canonical"]',
                () => {
                    const el = document.createElement('link');
                    el.setAttribute('rel', 'canonical');
                    return el;
                },
                url,
            ],
        ] as const;
        const previous = restore.map(([selector, make, value]) => ({
            selector,
            old: setMeta(selector, make, value),
        }));
        return () => {
            document.title = oldTitle;
            previous.forEach(({ selector, old }) => {
                const el = document.head.querySelector<HTMLElement>(selector);
                if (!el) return;
                if (old === null) el.remove();
                else if (el.tagName === 'LINK') el.setAttribute('href', old);
                else el.setAttribute('content', old);
            });
        };
    }, [title, description, path]);
    return null;
};
